import type React from "react";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useApi } from "../../hooks/api/useApi";
import { Link, useNavigate } from "react-router-dom";

const LoginForm: React.FC<{ path: string }> = ({ path }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const api = useApi();
  const isLoading = api.isLoading;

  const navigate = useNavigate();

  useEffect(() => {
    localStorage.clear();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const result = await api.post("access/login", {
      username: email,
      password,
    });

    if (result.statusCode !== "04") {
      localStorage.setItem("accessKey", result?.data?.accessKey);

      !path.includes("admin")
        ? navigate("/login?type=otp")
        : navigate("/admin/login?type=otp");
    } else {
      setError("Login failed. Please check your credentials.");
    }
  };

  return (
    <div className="flex w-full items-center justify-center  md:p-8 lg:p-16">
      <motion.div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg sm:p-8"
        initial={{ x: 100, opacity: 0, scale: 0.8 }}
        animate={{ x: 0, opacity: 1, scale: 1 }}
        transition={{
          type: "spring",
          stiffness: 100,
          damping: 15,
          delay: 0.1,
        }}
      >
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">
            {!path.includes("admin")
              ? "Login to your account"
              : "Login to your account(Admin)"}
          </h1>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-500">
            {error}
          </div>
        )}

        {api.error && !error && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-500">
            {api.error.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label
              htmlFor="email"
              className="text-sm font-medium text-gray-700"
            >
              Email address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none"
              placeholder="Email address"
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="password"
              className="text-sm font-medium text-gray-700"
            >
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 pr-10 text-gray-900 placeholder-gray-400 focus:outline-none"
                placeholder="Password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <motion.button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-md bg-red-500 py-2.5 text-center font-medium text-white shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Logging in...
              </span>
            ) : (
              "Login"
            )}
          </motion.button>

          <div className="text-center">
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-red-600 hover:text-red-500"
            >
              Forgot password?
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default LoginForm;
